// eslint-disable-next-line import/no-unresolved
import { TextItem } from 'pdfjs-dist/types/src/display/api';
import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { IChatMessage } from '@/interfaces/deltaGpt/IChatMessage.ts';
import { IDocumentSimple } from '@/interfaces/deltaGpt/IDocument.ts';

export const useDeltaGptStore = defineStore('deltaGptStore', () => {
    const allDocumentsSimple = shallowRef<Array<IDocumentSimple>>([]);
    const subscriptionFilters = ref<Array<string> | null>(null);

    function parseReferences (source: string): Record<string, Array<TextItem>> {
        const textItemsByPage: Record<string, Array<TextItem>> = {};
        const lines = source.split('\n');
        lines.forEach((line) => {
            const lineReferences = line.match(/\{([A-Za-z0-9_]+\d+):(\d+)(.{1,})\}/g);
            if (lineReferences?.length) {
                const lineReference = lineReferences[0];
                const transform = lineReference.match(/\(.{1,}\)/g);
                if (!transform?.length) return;
                const pageAndLine = lineReference
                    .replace(transform?.[0] ?? '', '')
                    .replace('{', '').replace('}', '')
                    .split('_')[1]
                    .split(':');

                const page = parseInt(pageAndLine[0], 10);
                const lineNumber = parseInt(pageAndLine[1], 10);
                if (!textItemsByPage[page]) {
                    textItemsByPage[page] = [];
                }

                const textItemArray = transform![0].replace('(', '').replace(')', '').split('_');
                const transformArray = textItemArray[0].split(',');
                textItemsByPage[page][lineNumber] = {
                    transform: [
                        parseFloat(transformArray[0]),
                        parseFloat(transformArray[1]),
                        parseFloat(transformArray[2]),
                        parseFloat(transformArray[3]),
                        parseFloat(transformArray[4]),
                        parseFloat(transformArray[5]),
                    ],
                    width: parseFloat(textItemArray[1]),
                    height: parseFloat(textItemArray[2]),
                    str: line.replace(lineReference, ''),
                    dir: textItemArray[3],
                    hasEOL: false,
                    fontName: '',
                };
            }
        });
        return textItemsByPage;
    }

    function fetchCrossDocumentChat (conversationId: string, messages: Array<IChatMessage>, callback: (text: string) => void): Promise<void> {
        return deltaGptApi.crossDocumentChat(conversationId, messages, callback);
    }

    function fetchDocumentChat (documentShortCode: string, conversationId: string, messages: Array<IChatMessage>, callback: (text: string) => void): Promise<void> {
        return deltaGptApi.documentChat(documentShortCode, conversationId, messages, callback);
    }

    async function fetchDocumentReferences (documentShortCode: string): Promise<Record<string, Array<TextItem>>> {
        const references = await deltaGptApi.getDocumentReferences(documentShortCode);
        return parseReferences(references);
    }

    async function fetchPageReferences (documentShortCode: string, page: number): Promise<Array<TextItem>> {
        const references = await deltaGptApi.getPageReferences(documentShortCode, page);
        const parsedReferences = parseReferences(references);
        return parsedReferences[page.toString()];
    }

    async function fetchPdf (documentShortCode: string, shareId?: string | undefined): Promise<Uint8Array> {
        return deltaGptApi.getPdf(documentShortCode, shareId);
    }

    async function fetchPdfPage (documentShortCode: string, page: number): Promise<Uint8Array> {
        return deltaGptApi.getPdfPage(documentShortCode, page);
    }

    async function fetchAllDocumentInfo (): Promise<void> {
        allDocumentsSimple.value = await deltaGptApi.getAllDocumentInfo();
    }

    function updateDocumentCommentCount (documentShortCode: string, commentCount: number): void {
        allDocumentsSimple.value = allDocumentsSimple.value.map(document => (
            document.documentShortCode === documentShortCode
                ? { ...document, commentCount }
                : document
        ));
    }

    async function fetchSubscriptionFiltersIfNeeded (): Promise<void> {
        if (!subscriptionFilters.value) {
            subscriptionFilters.value = await deltaGptApi.getSubscriptionFilters();
        }
    }

    async function getEmbeddedSearchResults (search: string, documentIds?: Array<string>): Promise<Array<IDocumentSimple>> {
        const resultsIds = await deltaGptApi.getEmbeddingsSearch(search, 'embeddings', documentIds);
        return resultsIds.reduce((acc: Array<IDocumentSimple>, id) => {
            const doc = allDocumentsSimple.value.find(d => d.documentShortCode === id);
            if (doc) acc.push(doc);
            return acc;
        }, []);
    }

    return {
        fetchCrossDocumentChat,
        fetchDocumentChat,
        fetchDocumentReferences,
        fetchPageReferences,
        fetchPdf,
        fetchPdfPage,
        fetchAllDocumentInfo,
        updateDocumentCommentCount,
        allDocumentsSimple,
        getEmbeddedSearchResults,
        fetchSubscriptionFiltersIfNeeded,
        subscriptionFilters,
    };
});
